import { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Button,
  Table,
  Box,
  Loader,
  Center,
  ActionIcon,
  Menu,
  Alert,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { Plus, MoreVertical, Edit, Trash2, BookOpen, Info } from 'react-feather';
import { frameworksApi, type Framework } from '../../../services/api/frameworks';
import { getApiErrorMessage } from '../../../services/api/client';
import { EmptyState } from '../../../components/common';
import { FrameworkFormModal } from './FrameworkFormModal';

export function FrameworksPage() {
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [loading, setLoading] = useState(true);

  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [selectedFramework, setSelectedFramework] = useState<Framework | null>(null);

  // AC1 — load the full list.
  const fetchFrameworks = useCallback(async () => {
    try {
      setLoading(true);
      const result = await frameworksApi.getAll();
      setFrameworks(result);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: getApiErrorMessage(error, 'Failed to fetch frameworks'),
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFrameworks();
  }, [fetchFrameworks]);

  const handleAdd = () => {
    setSelectedFramework(null);
    openModal();
  };

  const handleEdit = (framework: Framework) => {
    setSelectedFramework(framework);
    openModal();
  };

  // AC2 / AC3 — refresh the list in place (no page reload) after a save.
  const handleSaved = () => {
    fetchFrameworks();
    notifications.show({
      title: 'Success',
      message: selectedFramework ? 'Framework updated' : 'Framework added',
      color: 'green',
    });
  };

  // AC5 — delete with a confirmation step first.
  const handleDelete = (framework: Framework) => {
    modals.openConfirmModal({
      title: 'Delete Framework',
      children: (
        <Text size="sm">
          Are you sure you want to delete <strong>{framework.name}</strong>? The AI will no longer
          use it as a canonical spelling reference.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await frameworksApi.delete(framework.id);
          notifications.show({
            title: 'Success',
            message: 'Framework deleted',
            color: 'green',
          });
          fetchFrameworks();
        } catch (error) {
          notifications.show({
            title: 'Error',
            message: getApiErrorMessage(error, 'Failed to delete framework'),
            color: 'red',
          });
        }
      },
    });
  };

  // Lower-cased names for the modal's instant duplicate check (AC6).
  const existingNames = frameworks.map((f) => f.name.trim().toLowerCase());

  const infoBanner = (
    <Alert icon={<Info size={16} />} color="teal" mb="lg">
      Frameworks are a global glossary of named models and acronyms (e.g. &ldquo;LADA&rdquo;). The
      AI uses these canonical spellings so generated content matches how you name things, instead of
      guessing phonetically from a transcript.
    </Alert>
  );

  // Empty state (no frameworks yet).
  if (!loading && frameworks.length === 0) {
    return (
      <Container size="xl" py="xl">
        <Title order={1} mb="xl">
          Frameworks
        </Title>
        {infoBanner}
        <EmptyState
          icon={<BookOpen size={40} strokeWidth={1.5} />}
          title="No frameworks yet"
          description="Add your first framework so the AI spells it correctly in generated content."
          action={
            <Button leftSection={<Plus size={18} />} onClick={handleAdd}>
              Add Framework
            </Button>
          }
          size="lg"
          withCard
        />

        <FrameworkFormModal
          opened={modalOpened}
          onClose={closeModal}
          framework={selectedFramework}
          existingNames={existingNames}
          onSaved={handleSaved}
        />
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      {/* Header */}
      <Group justify="space-between" mb="md">
        <Box>
          <Title order={1}>Frameworks</Title>
          <Text c="dimmed" size="sm" mt={4}>
            {frameworks.length} {frameworks.length === 1 ? 'framework' : 'frameworks'} in the
            glossary
          </Text>
        </Box>
        <Button leftSection={<Plus size={18} />} onClick={handleAdd}>
          Add Framework
        </Button>
      </Group>

      {infoBanner}

      {/* Content */}
      {loading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : (
        <Card withBorder p={0} styles={{ root: { boxShadow: 'none' } }}>
          <Table.ScrollContainer minWidth={600}>
            <Table verticalSpacing="sm" horizontalSpacing="md">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: '30%' }}>
                    <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                      Name
                    </Text>
                  </Table.Th>
                  <Table.Th>
                    <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                      Description
                    </Text>
                  </Table.Th>
                  <Table.Th style={{ width: 60 }}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {frameworks.map((framework) => (
                  <Table.Tr key={framework.id}>
                    <Table.Td>
                      <Text fw={500} size="sm">
                        {framework.name}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {framework.description ? (
                        <Text size="sm">{framework.description}</Text>
                      ) : (
                        <Text c="dimmed" size="sm">
                          —
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Menu shadow="md" width={160} position="bottom-end">
                        <Menu.Target>
                          <ActionIcon variant="subtle" color="gray">
                            <MoreVertical size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item
                            leftSection={<Edit size={14} />}
                            onClick={() => handleEdit(framework)}
                          >
                            Edit
                          </Menu.Item>
                          <Menu.Item
                            leftSection={<Trash2 size={14} />}
                            color="red"
                            onClick={() => handleDelete(framework)}
                          >
                            Delete
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}

      <FrameworkFormModal
        opened={modalOpened}
        onClose={closeModal}
        framework={selectedFramework}
        existingNames={existingNames}
        onSaved={handleSaved}
      />
    </Container>
  );
}

export default FrameworksPage;
